import { makeAutoObservable } from "mobx";
import { dashboardOverviewStore } from "./target-data-overview.store";
import { dashboardEsgDataTableStore } from "./esg-data-table.store";

const MUTEX_FIELDS = ["years", "bus", "countries"];
const SECTIONS = new Set(["metric", "organization", "esg", "charts"]);
const OVERVIEW_SECTIONS = new Set(["metric", "organization"]);

const VIEW_MULTI_FIELDS = {
  year: new Set(["years", "bus", "countries"]),
  bu: new Set(["bus", "countries"]),
  country: new Set(["bus", "countries"]),
};

const DEFAULTS = {
  section: "organization",
  lastOverviewSection: "organization",
  filters: { years: [], bus: [], countries: [], categories: [] },
  cleared: { years: false, bus: false, countries: false, categories: false },
  view: "year",
};

class DashboardDataTableStore {
  section = DEFAULTS.section;
  lastOverviewSection = DEFAULTS.lastOverviewSection;
  filters = { ...DEFAULTS.filters };
  cleared = { ...DEFAULTS.cleared };
  view = DEFAULTS.view;

  constructor() {
    makeAutoObservable(this);
  }

  setSection = (next) => {
    if (!SECTIONS.has(next)) return;
    this.section = next;
    if (OVERVIEW_SECTIONS.has(next)) {
      this.lastOverviewSection = next;
      dashboardOverviewStore.setGroupBy(next);
    }
  };

  setTab = (tab) => {
    if (tab === "overview") {
      this.setSection(this.lastOverviewSection);
    } else if (tab === "esg" || tab === "charts") {
      this.setSection(tab);
    }
  };

  get tab() {
    return OVERVIEW_SECTIONS.has(this.section) ? "overview" : this.section;
  }
  get isOverviewActive() {
    return OVERVIEW_SECTIONS.has(this.section);
  }
  get isDataTableActive() {
    return !OVERVIEW_SECTIONS.has(this.section);
  }
  get overviewSection() {
    return OVERVIEW_SECTIONS.has(this.section) ? this.section : null;
  }
  get dataTableSection() {
    return OVERVIEW_SECTIONS.has(this.section) ? null : this.section;
  }

  isMultiSelect = (field) => VIEW_MULTI_FIELDS[this.view]?.has(field) ?? false;
  isLockedToSingle = (field) =>
    MUTEX_FIELDS.includes(field) && !this.isMultiSelect(field);

  setView = (next) => {
    if (next !== "year" && next !== "bu" && next !== "country") return;
    this.view = next;
    for (const f of MUTEX_FIELDS) {
      if (!this.isMultiSelect(f) && this.filters[f].length > 1) {
        this.filters[f] = this.filters[f].slice(0, 1);
      }
    }
  };

  setCategories = (values) => {
    const next = Array.isArray(values) ? [...values] : [];
    this.filters.categories = next;
    this.cleared.categories = false;
    dashboardEsgDataTableStore.setPage(1);
  };

  clearCategoriesExplicit = () => {
    this.filters.categories = [];
    this.cleared.categories = true;
    dashboardEsgDataTableStore.setPage(1);
  };

  setFilter = (field, values) => {
    if (!MUTEX_FIELDS.includes(field)) return;
    const next = Array.isArray(values) ? [...values] : [];
    this.filters[field] = this.isLockedToSingle(field)
      ? next.slice(0, 1)
      : next;
    this.cleared[field] = false;
  };

  clearFilterExplicit = (field) => {
    if (!MUTEX_FIELDS.includes(field) && field !== "categories") return;
    this.filters[field] = [];
    this.cleared[field] = true;
  };

  clearAll = (field) => {
    if (!MUTEX_FIELDS.includes(field)) return;
    this.filters[field] = [];
    this.cleared[field] = false;
  };

  clearFilters = () => {
    this.filters = { ...DEFAULTS.filters };
    this.cleared = { ...DEFAULTS.cleared };
  };

  reset = () => {
    this.section = DEFAULTS.section;
    this.lastOverviewSection = DEFAULTS.lastOverviewSection;
    this.filters = { ...DEFAULTS.filters };
    this.cleared = { ...DEFAULTS.cleared };
    this.view = DEFAULTS.view;
  };
}

export const dashboardDataTableStore = new DashboardDataTableStore();
